'use client';

/**
 * Inline field-validation primitives (2026-09-05).
 *
 * The rule: a missing / invalid REQUIRED field is surfaced on the field
 * itself — red border + a short message directly beneath it — never as a
 * toast. Toasts are for the outcome of an action (saved, failed, stopped);
 * they vanish, they don't say WHICH field, and they can't be seen when the
 * offending field sits on another tab.
 *
 *   const { errors, setError, clearError, validate } = useFieldErrors();
 *   ...
 *   if (!validate({ name: !name.trim() && 'Name is required' })) return;
 *   ...
 *   <Input className={fieldErrorClass(errors.name)} ... />
 *   <FieldError message={errors.name} />
 */

import * as React from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Red message rendered beneath an invalid field. Renders nothing when empty. */
export function FieldError({ message, className, id }: { message?: string | null; className?: string; id?: string }) {
  if (!message) return null;
  return (
    <p
      id={id}
      role="alert"
      className={cn('mt-1 flex items-start gap-1 text-[12px] leading-4 text-red-600 dark:text-red-400', className)}
    >
      <AlertCircle size={13} className="mt-px flex-shrink-0" aria-hidden />
      <span>{message}</span>
    </p>
  );
}

/** Border/ring classes for a control in the error state. Compose with the
 *  control's own classes: `className={cn(base, fieldErrorClass(errors.x))}`. */
export function fieldErrorClass(hasError?: string | boolean | null): string {
  return hasError
    ? '!border-red-500 focus:!ring-red-500 focus-visible:!ring-red-500 dark:!border-red-500'
    : '';
}

export type FieldErrors<K extends string = string> = Partial<Record<K, string>>;

/** Small state helper: keeps a map of field → message, clears entries as the
 *  user edits, and `validate()` sets every failing rule at once so the user
 *  sees all missing fields together instead of one per click. */
export function useFieldErrors<K extends string = string>(initial: FieldErrors<K> = {}) {
  const [errors, setErrors] = React.useState<FieldErrors<K>>(initial);

  const setError = React.useCallback((key: K, message: string | null | undefined) => {
    setErrors((prev) => {
      if (!message) {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (prev[key] === message) return prev;
      return { ...prev, [key]: message };
    });
  }, []);

  const clearError = React.useCallback((key: K) => setError(key, null), [setError]);
  const clearAll = React.useCallback(() => setErrors({}), []);

  /** rules: { field: false | '' | 'message' }. Returns true when valid. */
  const validate = React.useCallback((rules: Partial<Record<K, string | false | null | undefined>>): boolean => {
    const next: FieldErrors<K> = {};
    (Object.keys(rules) as K[]).forEach((k) => {
      const m = rules[k];
      if (typeof m === 'string' && m) next[k] = m;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  }, []);

  return { errors, setError, clearError, clearAll, validate, hasErrors: Object.keys(errors).length > 0 };
}

export default FieldError;
